import { useGetFormalOffer } from '@api/lendingAPI/lapiHooks';
import { Spinner } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import messages from './Success.messages';
import styles from './Success.module.css';
import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import { SuccessCircleSVG } from '../../../../svgComponents/successCircleSVG';
import { getDrawdownType } from '../../../drawDown/getDrawdownType';
import { DRAW_DOWN_MODE } from '../../../funding/ConfirmFundingView/ConfirmFunding.sagas';
import FallbackErrorModal from '../../fallbackErrorModal/FallbackErrorModal';
import { Content } from '../Content';
import { PROCESSING_PAYMENT_MODAL_RETURN_TO_DASHBOARD } from '../ProcessingPaymentModal.actions';

const mapStateToProps = (state: RootState) => ({
    ...state.processingPaymentModal
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    goToHome: () =>
        dispatch({ type: PROCESSING_PAYMENT_MODAL_RETURN_TO_DASHBOARD })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;
const Success = ({
    goToHome,
    senderLogo,
    receiverLogo
}: PropsFromRedux & {
    senderLogo: string;
    receiverLogo: string;
}) => {
    return (
        <Content
            title={<SuccessTitle />}
            icon={
                <span className={styles.processingDirectionSuccess}>
                    <SuccessCircleSVG
                        className={styles.SuccessCircleSVG}
                        data-testid="success-payment-icon"
                    />
                </span>
            }
            senderLogo={senderLogo}
            receiverLogo={receiverLogo}
            ctaOnClick={goToHome}
            ctaText={<FormattedMessage {...messages.returnToDashboard} />}
        />
    );
};

const SuccessTitle = connect(mapStateToProps)(({
    mode,
    amount,
    paymentMethod,
    offerId
}) => {
    const formattedAmount = <FormattedCurrency value={amount!} />;

    if (mode !== DRAW_DOWN_MODE) {
        return (
            <>
                <p>
                    <FormattedMessage
                        {...messages.repayConfirmed}
                        values={{
                            amount: formattedAmount,
                            paymentMethod: paymentMethod
                        }}
                    />
                </p>
                <p className={styles.subtitle}>
                    <FormattedMessage {...messages.repayConfirmedSubtitle} />
                </p>
            </>
        );
    }

    return (
        <DrawdownLoading formattedAmount={formattedAmount} offerId={offerId!} />
    );
});

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(Success);

const DrawdownLoading = ({
    formattedAmount,
    offerId
}: {
    formattedAmount: React.ReactNode;
    offerId: string;
}) => {
    const { formalOffer, loadingFormalOffer, formalOfferError } =
        useGetFormalOffer(offerId);

    if (loadingFormalOffer) return <Spinner />;
    if (formalOfferError) return <FallbackErrorModal />;

    const drawdownType = getDrawdownType(formalOffer!);

    switch (drawdownType) {
        case 'repayment_reduction':
            return (
                <FormattedMessage {...messages.repaymentReductionConfirmed} />
            );
        case 'interest_only_extension':
            return (
                <FormattedMessage
                    {...messages.interestOnlyExtensionConfirmed}
                />
            );
        case 'topup':
        default:
            return (
                <FormattedMessage
                    {...messages.drawDownConfirmed}
                    values={{ amount: formattedAmount }}
                />
            );
    }
};
